/**
 * HRImportManagement — Admin
 * Import des employés depuis les fichiers Meeko (Registre du personnel XLSX).
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Btn, inputCls } from '../ui';
import { Upload, Download, Users, CheckCircle, AlertCircle, X } from 'lucide-react';
import { getStructures, importHREmployees, downloadHRImportTemplate } from '../../api';

const HRImportManagement = () => {
  const [structures, setStructures] = useState([]);
  const [selectedStructureId, setSelectedStructureId] = useState('');
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    getStructures()
      .then(res => {
        setStructures(res.data || []);
        if (res.data?.length > 0) setSelectedStructureId(String(res.data[0].id));
      })
      .catch(() => {});
  }, []);

  const handleFileChange = (e) => {
    setFile(e.target.files[0] || null);
    setResult(null);
    setError(null);
  };

  const handleImport = useCallback(async () => {
    if (!file || !selectedStructureId) return;
    setLoading(true);
    setResult(null);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('structureId', selectedStructureId);
      const res = await importHREmployees(formData);
      setResult(res.data);
      setFile(null);
      // Reset file input
      const input = document.getElementById('hr-import-file');
      if (input) input.value = '';
    } catch (e) {
      setError(e.response?.data?.error || 'Erreur lors de l\'import');
    } finally {
      setLoading(false);
    }
  }, [file, selectedStructureId]);

  const handleDownloadTemplate = async () => {
    try {
      const res = await downloadHRImportTemplate();
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'template_import_rh.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      /* ignore */
    }
  };

  return (
<div className="px-4 sm:px-6 lg:px-8 space-y-6"><Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Users size={16} className="text-primary" />
          Import données RH
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">

        {/* Instructions */}
        <div className="bg-primary/5 border border-primary/20 rounded-lg px-4 py-3 text-sm text-gray-700 space-y-1">
          <p className="font-medium">Format accepté : Registre du personnel Meeko (.xlsx)</p>
          <p>Un fichier par structure (crèche). Les employés existants seront mis à jour.</p>
          <Btn variant="ghost" size="sm" onClick={handleDownloadTemplate} className="mt-1 px-0 text-primary">
            <Download size={14} />
            Télécharger le template CSV (import manuel)
          </Btn>
        </div>

        {/* Structure selector */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Structure cible *</label>
          <select
            className={inputCls}
            value={selectedStructureId}
            onChange={e => setSelectedStructureId(e.target.value)}
          >
            {structures.map(s => (
              <option key={s.id} value={String(s.id)}>{s.name}</option>
            ))}
          </select>
        </div>

        {/* File upload */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Fichier XLSX (Meeko) *</label>
          <input
            id="hr-import-file"
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileChange}
            className="block w-full text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-primary/10 file:text-primary hover:file:bg-primary/20 cursor-pointer"
          />
          {file && (
            <p className="text-xs text-gray-500 mt-1">{file.name} — {(file.size / 1024).toFixed(0)} Ko</p>
          )}
        </div>

        {/* Import button */}
        <button
          onClick={handleImport}
          disabled={!file || !selectedStructureId || loading}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition"
        >
          <Upload size={15} />
          {loading ? 'Import en cours...' : 'Importer les employés'}
        </button>

        {/* Error */}
        {error && (
          <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            <AlertCircle size={15} className="flex-shrink-0 mt-0.5" />
            {error}
          </div>
        )}

        {/* Result */}
        {result && (
          <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 space-y-2">
            <div className="flex items-center gap-2 text-green-700 font-medium text-sm">
              <CheckCircle size={16} />
              Import terminé — {result.total} employé{result.total > 1 ? 's' : ''} traité{result.total > 1 ? 's' : ''}
            </div>
            <div className="grid grid-cols-3 gap-3 text-center text-sm">
              <div className="bg-white rounded-lg px-3 py-2 border border-green-100">
                <div className="text-xl font-bold text-green-600">{result.created}</div>
                <div className="text-xs text-gray-500">Créés</div>
              </div>
              <div className="bg-white rounded-lg px-3 py-2 border border-green-100">
                <div className="text-xl font-bold text-blue-600">{result.updated}</div>
                <div className="text-xs text-gray-500">Mis à jour</div>
              </div>
              <div className="bg-white rounded-lg px-3 py-2 border border-green-100">
                <div className="text-xl font-bold text-red-500">{result.errors?.length || 0}</div>
                <div className="text-xs text-gray-500">Erreurs</div>
              </div>
            </div>
            {result.errors?.length > 0 && (
              <div className="mt-2">
                <p className="text-xs font-medium text-gray-600 mb-1">Détail des erreurs :</p>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {result.errors.map((e, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-red-700">
                      <X size={12} className="flex-shrink-0" />
                      <span className="font-medium">{e.name}</span> — {e.reason}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card></div>
  );
};

export default HRImportManagement;
